import { AnyAction, Store } from "redux";
import { RSAA, RSAAAction } from "redux-api-middleware";

import {
  getAccessToken,
  setAccessToken,
  saveRefreshTokenPromise,
  clearRefreshTokenPromise,
} from "../api/gateway/auth/auth.api";

const isRefreshCall = (action: RSAAAction, refreshAction: RSAAAction) =>
  (action[RSAA].endpoint as string).includes(
    refreshAction[RSAA].endpoint as string,
  ) && action[RSAA].method === refreshAction[RSAA].method;

/**
 * Checks whether the response is 401 due to the expired access token,
 * requests for the new access token and retries the previously failed
 * request with the new access token in the authorization header.
 */
export const attemptRefresh = (settings: {
  action: RSAAAction<unknown, unknown, AnyAction>;
  authHeaderSupplier: () => unknown;
  failure: () => unknown;
  next: (obj: unknown) => Promise<unknown>;
  refreshReducerKey?: string;
  store: Store;
}) => {
  const {
    action,
    authHeaderSupplier,
    failure,
    next,
    refreshReducerKey = "auth",
    store,
  } = settings;

  return (response: { error: unknown; payload: { status: number } }) => {
    // The API call returned unauthorized user (access token is expired)
    if (
      response &&
      response.error &&
      // refresh access on both 401 and 403 (it wont hurt that much)
      response.payload.status === 401 &&
      !isRefreshCall(action, getAccessToken())
    ) {
      if (store.getState()[refreshReducerKey]?.error) {
        return failure();
      }
      // We check if there is already dispatched a call to refresh the token,
      // if so we can simply queue the call until the refresh request completes
      let refreshPromise =
        store.getState()[refreshReducerKey]?.refreshTokenPromise;

      if (!refreshPromise) {
        // play the refresh RSAA from scratch
        refreshPromise = store.dispatch(getAccessToken());
        next(saveRefreshTokenPromise(refreshPromise));
      }

      return refreshPromise.then(
        (resp: { error: unknown; payload: unknown }) => {
          next(clearRefreshTokenPromise());

          // Refresh was successful
          if (!resp.error) {
            next(setAccessToken(resp.payload));

            const authHeaderValue = authHeaderSupplier();
            if (authHeaderValue) {
              // @ts-expect-error have no clue how to type this
              action[RSAA].headers.Authorization = authHeaderValue;
            }

            // replay the queued actions
            return store.dispatch(action);
          }

          return failure();
        },
      );
    }

    return response;
  };
};
